import { Inject, Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { EMPTY, Observable } from 'rxjs'
import { AuthBackendService } from '../../auth-backend.service'
import { UserContextActions } from './action-types'
import { ProjectDependentDataService } from '../../../../core/services/project-dependent-data.service'
import { catchError, concatMap, filter, map, switchMap, take } from 'rxjs/operators'
import { IntervalPromiseStatusEnum } from '../../../../shared/enums/interval-promise-status.enum'
import { GoogleAnalyticsService } from '../../../monitoring/google-analytics.service'
import { HeaderMenuService } from '../../../layout/services/header-menu.service'
import { selectAccessToken } from '../../state/auth.selectors'
import { MainViewActions } from '../../../layout/state/action-types'
import { selectProjectKey } from './user-context.selectors'
import { BuildInfo } from '../../../../constants/buildInfo.constant'
import { ApplicationVersionService } from '../../../../shared/services/applicationVersion.service'
import { DialogService } from '../../../../core/services/dialog.service'

@Injectable()
export class UserContextEffects {

    setProject$: Observable<any>
    setProjectAfterRefreshAccessToken$: Observable<any>
    setProjectKeys$: Observable<any>
    reset$: Observable<any>

    private stateUpdatePromise

    constructor(@Inject(Actions) private actions$: Actions,
                @Inject(ApplicationVersionService) private applicationVersionService: ApplicationVersionService,
                @Inject(AuthBackendService) private authBackendService: AuthBackendService,
                @Inject(DialogService) private dialogService: DialogService,
                @Inject(GoogleAnalyticsService) private googleAnalyticsService: GoogleAnalyticsService,
                @Inject(HeaderMenuService) private headerMenuService: HeaderMenuService,
                @Inject(Store) private store: Store,
                @Inject(ProjectDependentDataService) private projectDependentDataService: ProjectDependentDataService,
                ) {

        this.setProject$ = createEffect(() => this.actions$.pipe(
            ofType(UserContextActions.setProject),
            switchMap((action) => {
                this.projectDependentDataService.resetProjectData()
                this.googleAnalyticsService.setProject(action.project)

                return this.store.select(selectAccessToken).pipe(
                    take(1),
                    concatMap(accessToken => {
                        if (accessToken.token != null) {
                            this.getStateUpdate()
                        }

                        if (!this.stateUpdatePromise || this.stateUpdatePromise?.$$state?.status === IntervalPromiseStatusEnum.Cancelled) {
                            this.stateUpdatePromise = setInterval(this.getStateUpdate, 30000)
                        }

                        return [
                            MainViewActions.setAllIsEditingFalse(),
                            MainViewActions.resetNotifications(),
                            MainViewActions.resetNotificationsRead(),
                            MainViewActions.resetUnreadNotificationsCount(),
                            MainViewActions.resetReadNotificationsCount(),
                            MainViewActions.resetBulkOperations(),
                            MainViewActions.resetExistContractBulkOperation(),
                            // UserContextActions.userContextSetProject({
                            //     project: action.project
                            // })
                        ]
                    })
                )
            })
        ))

        this.setProjectAfterRefreshAccessToken$ = createEffect(() => this.actions$.pipe(
            ofType(UserContextActions.setProjectAfterRefreshAccessToken),
            map((action) => {
                this.projectDependentDataService.resetProjectData()

                return UserContextActions.userContextSetProject({
                    project: action.project
                })
            })
        ))

        this.setProjectKeys$ = createEffect(() => this.actions$.pipe(
            ofType(UserContextActions.setProjectKeys),
            switchMap(action => {
                return this.authBackendService.getProjects(action.projectKeys).pipe(
                    concatMap((data) => {
                        return [
                            UserContextActions.userContextSetProjectKeys({
                                keys: action.projectKeys
                            }),
                            UserContextActions.userContextSetProjects({
                                projects: data
                            })
                        ]
                    }),
                    catchError(error => {
                        console.warn('setProjectKeys$ failed:', error?.status)

                        return EMPTY
                    })
                )
            })
        ))

        this.reset$ = createEffect(() => this.actions$.pipe(
            ofType(UserContextActions.reset),
            map(() => {
                if (this.stateUpdatePromise) {
                    if (typeof this.stateUpdatePromise === 'object' && 'cancel' in this.stateUpdatePromise) {
                        this.stateUpdatePromise.cancel() // The cancel not set variable to NULL but keep promise with update state
                    } else {
                        clearInterval(this.stateUpdatePromise) // If it's a number, it should be an interval ID
                    }
                    this.stateUpdatePromise = null // Explicitly set the variable to null
                }

                return UserContextActions.userContextReset()
            })
        ))
    }

    private getStateUpdate = (): void => {
        this.store.select(selectProjectKey).pipe(
            filter(key => key !== undefined && key !== null),
            take(1),
            switchMap(key => this.headerMenuService.getStateUpdate(key))
        ).subscribe(update  => {
            this.checkNewVersion(update.applicationVersion)

            this.store.dispatch(MainViewActions.updateImportantMessages({
                messages: update.messages
            }))

            this.store.dispatch(MainViewActions.setUnreadNotificationsCount({
                count: update.unreadNotificationsCount
            }))

            this.store.dispatch(MainViewActions.setReadNotificationsCount({
                count: update.readNotificationsCount
            }))

            this.store.dispatch(MainViewActions.setBlockRequests({
                blockRequests: update.blockRequests
            }))

            this.store.dispatch(MainViewActions.setExistContractBulkOperation({
                existContractBulkOperation: update.existContractBulkOperation
            }))

            this.store.dispatch(MainViewActions.setCuzkPasswordValidityState({
                cuzkPasswordValidityState: update.cuzkPasswordValidityState
            }))

            this.store.dispatch(MainViewActions.setSkippedDateDueCuzkPasswordValidity({
                skippedDateDueCuzkPasswordValidity: update.skippedDateDueCuzkPasswordValidity
            }))
        }, () => {
            // Error handled by auth service — token refresh will redirect to login
        })
    }

    private checkNewVersion(backendVersion): void {
        let frontendVersion = BuildInfo.appVersion

        if (!this.applicationVersionService.isFrontendVersionActual(frontendVersion, backendVersion)) {
            this.dialogService.showSimpleDialog(
                    'Upozornění',
                    ['Byla nasazena nová verze aplikace. Webová stránka bude znovu načtena.'],
                    'Rozumím')
                .subscribe(() => window.location.reload())
        }
    }
}